<template>
  <div class="collection-customer-mobile px-4 py-3 overflow-y-auto" style="max-height: calc(100vh - 56px); -webkit-overflow-scrolling: touch;">

    <!-- Top Action Row -->
    <div class="flex flex-col gap-3 mb-4">
      <!-- Add + Export Buttons -->
      <div class="flex gap-2">
        <!-- Add Customer — hidden when caseConfig is set (same as desktop) -->
        <v-btn
          v-if="!parentPage.caseConfig?._id"
          @click="parentPage.addNewCustomer"
          v-iam-disabled="`${parentPage.permissionPrefix}.write`"
          :color="$vuetify.theme.currentTheme.primary"
          class="normal-case white--text flex-1 rounded-lg"
          depressed
        >
          <v-icon left dark>mdi-plus</v-icon>
          Add
        </v-btn>

        <!-- Export — always visible -->
        <v-btn
          @click="parentPage.exportToExcel()"
          v-iam-disabled="`${parentPage.permissionPrefix}.export`"
          :color="$vuetify.theme.currentTheme.primary"
          class="normal-case white--text flex-1 rounded-lg"
          depressed
        >
          <v-icon left>mdi-download</v-icon>
          Export
        </v-btn>
      </div>

      <!-- Entity Filter -->
      <div v-if="parentPage.isEntityEnabled" class="w-full">
        <v-autocomplete
          v-model="parentPage.entityId"
          :label="`Filter by ${parentPage.entityTitle}`"
          :placeholder="`Select ${parentPage.entityTitle}`"
          @update:search-input="parentPage.getAllEntities"
          :search-input.sync="parentPage.searchValue"
          :items="parentPage.entities"
          item-text="entityName"
          item-value="_id"
          hide-details
          outlined
          dense
          clearable
          class="rounded-lg bg-white"
          @change="parentPage.handleInput"
        ></v-autocomplete>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="parentPage.loadTable" class="flex justify-center py-12">
      <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
    </div>

    <!-- Customer Card List -->
    <template v-else-if="parentPage.customers && parentPage.customers.length > 0">
      <div
        v-for="item in parentPage.customers"
        :key="item._id"
        class="customer-card mb-3 rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm"
      >
        <!-- Card Header: Name, CRN, Checkbox -->
        <div class="px-4 pt-3 pb-2 bg-gray-50 flex justify-between items-center border-b">
          <div>
            <div
              class="text-sm font-bold text-blue-600 hover:underline cursor-pointer"
              @click="parentPage.showCustomerDetails(item)"
            >
              {{ parentPage.userFullName(item) }}
            </div>
            <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
              CRN: {{ parentPage.maskNumber(item.crn) }}
            </span>
          </div>
          <v-checkbox
            v-model="parentPage.selected"
            :value="item"
            hide-details
            dense
            class="mt-0 pt-0"
          ></v-checkbox>
        </div>

        <!-- Card Body: Details -->
        <div class="px-4 py-3">
          <div class="flex flex-col gap-1 text-xs">
            <div v-if="parentPage.isEntityEnabled" class="flex justify-between py-1.5 border-b border-gray-50">
              <span class="text-gray-400 font-medium">{{ parentPage.entityTitle }}</span>
              <span class="font-bold text-gray-700 text-right">{{ item.entity?.entityName || '-' }}</span>
            </div>
            <div class="flex justify-between py-1.5 border-b border-gray-50">
              <span class="text-gray-400 font-medium">Email</span>
              <span class="font-semibold text-gray-700 text-right break-all max-w-[55%]">{{ item.customerEmail || '-' }}</span>
            </div>
            <div class="flex justify-between py-1.5 border-b border-gray-50">
              <span class="text-gray-400 font-medium">Phone</span>
              <span class="font-bold text-gray-700">{{ item.customerMobile?.[0] ? parentPage.maskNumber(item.customerMobile[0]) : '-' }}</span>
            </div>
            <div class="flex flex-col py-1.5">
              <span class="text-gray-400 font-medium mb-0.5">Address</span>
              <span class="font-semibold text-gray-700">
                <template v-if="item.customerAddress && item.customerAddress.length > 0">
                  {{ parentPage.customerAddress(item.customerAddress[0]) || '-' }}
                  <span class="text-gray-400" v-if="item.customerAddress[0]?.typeOfAddress">
                    ({{ item.customerAddress[0]?.typeOfAddress }})
                  </span>
                </template>
                <template v-else>-</template>
              </span>
            </div>
          </div>
        </div>

        <!-- Card Footer: Actions -->
        <div class="px-4 py-2 bg-gray-50 border-t flex justify-end gap-2">
          <v-btn
            v-if="parentPage.hasIamPermission(parentPage.iamStatements, `${parentPage.permissionPrefix}.write`)"
            x-small
            outlined
            color="primary"
            class="rounded-lg normal-case"
            @click="parentPage.customerEdit(item)"
          >
            <v-icon left size="12">mdi-pencil</v-icon>
            Edit
          </v-btn>
          <v-btn
            v-if="parentPage.hasIamPermission(parentPage.iamStatements, `${parentPage.permissionPrefix}.delete`)"
            x-small
            outlined
            color="red"
            class="rounded-lg normal-case"
            @click="parentPage.customerDelete(item)"
          >
            <v-icon left size="12">mdi-delete</v-icon>
            Delete
          </v-btn>
        </div>
      </div>

      <!-- Pagination — always show when there are items so user can navigate -->
      <div class="flex justify-center pt-2 pb-4">
        <v-pagination
          v-model="parentPage.options.page"
          :length="totalPages"
          total-visible="5"
          :color="$vuetify.theme.currentTheme.primary"
        ></v-pagination>
      </div>
    </template>

    <!-- Empty State -->
    <div v-else class="text-center py-14 px-4">
      <v-icon size="52" color="grey lighten-2">mdi-account-off-outline</v-icon>
      <div class="text-sm font-semibold text-gray-500 mt-3">No Customers Found</div>
      <div class="text-xs text-gray-400 mt-1">Try refining search or filters</div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'CollectionCustomer_mobile',
  inject: ['parentPage'],
  computed: {
    totalPages() {
      const limit = this.parentPage.options.itemsPerPage || 10
      return Math.ceil((this.parentPage.totalItems || 0) / limit) || 1
    }
  },
  mounted() {
    // Trigger initial fetch if customers haven't been loaded yet
    if (!this.parentPage.customers || this.parentPage.customers.length === 0) {
      this.parentPage.fetchCustomers()
    }
  }
}
</script>

<style scoped>
.collection-customer-mobile {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.customer-card {
  border-radius: 12px !important;
  transition: box-shadow 0.2s ease-in-out;
}
.customer-card:active {
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2) !important;
}
</style>
